import { CSSProperties, ReactNode } from 'react'
import { Row } from '../schema/document'
import { PageElement } from '../schema/elements'
import { RenderContext } from './context'
import ElementRenderer from './Elements'

/**
 * The one renderer, used by the editor and by the published page.
 *
 * §5 of the brief: two modes, one implementation. `edit` adds ids and data
 * attributes so the editor can find what was clicked; the chrome itself —
 * outlines, row toolbars, everything made of MUI — arrives through
 * `renderRowChrome` from the outside. That is what keeps this file renderable
 * in Node while the editor around it stays an ordinary part of the RMS app.
 */

export interface BlockRendererProps {
  blocks: Row[]
  context: RenderContext
  /*
    Three slots, all edit-only and all filled from outside. They are what let the
    editor put MUI toolbars, selection outlines and "add element" buttons around
    the very same components the published page uses, without this file ever
    importing any of it.
  */
  renderRowChrome?: (row: Row, children: ReactNode) => ReactNode
  renderItemChrome?: (item: PageElement, children: ReactNode) => ReactNode
  /** Appended inside every column, after its items. */
  renderColumnExtra?: (row: Row, columnId: string) => ReactNode
}

function rowClassName(row: Row): string {
  return [
    'mp-row',
    `mp-row--layout-${row.layout}`,
    `mp-row--pad-${row.paddingY}`,
    `mp-row--gap-${row.gap}`,
    `mp-row--valign-${row.verticalAlign}`,
    row.maxWidth === 'full' ? 'mp-row--full' : 'mp-row--contained',
    row.mobileBehavior === 'keep2' ? 'mp-row--mobile-keep2' : 'mp-row--mobile-stack',
    row.background.type === 'color' ? `mp-row--bg-${row.background.slot}` : '',
    row.background.type === 'image' ? `mp-row--bg-image mp-row--overlay-${row.background.overlay}` : '',
    row.hidden ? 'mp-row--hidden' : ''
  ]
    .filter(Boolean)
    .join(' ')
}

function RowRenderer({
  row,
  context,
  renderItemChrome,
  renderColumnExtra
}: {
  row: Row
  context: RenderContext
  renderItemChrome?: BlockRendererProps['renderItemChrome']
  renderColumnExtra?: BlockRendererProps['renderColumnExtra']
}) {
  // The URL is computed, which is the one case CLAUDE.md leaves open for an
  // inline style. Everything else about the background is a class.
  const backgroundStyle: CSSProperties | undefined =
    row.background.type === 'image'
      ? { backgroundImage: `url(${context.resolveAssetUrl(row.background.assetId)})` }
      : undefined

  return (
    <section
      // The row's id in every mode, not only the editor's: the template's
      // preview finds the section that was clicked by it, and on the
      // published page it is an anchor and nothing more.
      id={row.id}
      className={rowClassName(row)}
      style={backgroundStyle}
      data-mp-row={context.mode === 'edit' ? row.id : undefined}
    >
      <div className="mp-row__inner">
        {row.columns.map(column => (
          <div
            key={column.id}
            className={`mp-col mp-align-${column.align}`}
            data-mp-col={context.mode === 'edit' ? column.id : undefined}
          >
            {column.items.map(item => {
              const rendered = (
                <div
                  key={item.id}
                  className="mp-item"
                  data-mp-item={context.mode === 'edit' ? item.id : undefined}
                >
                  <ElementRenderer element={item} ctx={context} />
                </div>
              )
              return renderItemChrome ? (
                <div key={item.id}>{renderItemChrome(item, rendered)}</div>
              ) : (
                rendered
              )
            })}
            {renderColumnExtra?.(row, column.id)}
          </div>
        ))}
      </div>
    </section>
  )
}

/**
 * A row made only of maps that have no Google place yet. The editor draws
 * their placeholders; a published page has nothing to put there, and an
 * empty band of padding would read as a broken page.
 */
const awaitsData = (row: Row) => {
  const items = row.columns.flatMap(column => column.items)
  return (
    items.length > 0 &&
    items.every(item => item.type === 'map' && !item.embedUrl)
  )
}

function BlockRenderer(props: BlockRendererProps) {
  const { blocks, context, renderRowChrome, renderItemChrome, renderColumnExtra } =
    props
  const isEdit = context.mode === 'edit'

  return (
    <>
      {blocks.map(row => {
        // A hidden row still exists for the person editing it; a visitor must
        // never receive it, not even as display:none, because "hidden" is how
        // an owner takes something down.
        if (row.hidden && !isEdit) return null
        if (!isEdit && !context.placeholders && awaitsData(row)) return null

        const rendered = (
          <RowRenderer
            key={row.id}
            row={row}
            context={context}
            renderItemChrome={renderItemChrome}
            renderColumnExtra={renderColumnExtra}
          />
        )
        if (isEdit && renderRowChrome) {
          return <div key={row.id}>{renderRowChrome(row, rendered)}</div>
        }
        return rendered
      })}
    </>
  )
}

export default BlockRenderer
